import React, { useEffect, useState } from 'react';
import { Check, ChevronLeft } from 'react-bootstrap-icons';
import { useTranslation } from 'next-i18next';
import { FilterOptionKeys, FilterOptions, FilterOptionValue, MappedFacet, SearchParams } from '@mybestbrands/types';
import { Divider } from '@/app/ui/components/filters/mobile/Divider';
import styles from './style.module.scss';
import clsx from 'clsx';
import ColorBox from '@/app/ui/components/filters/colorbox';
import Image from 'next/image';
import PriceFilterSlider from '@/app/ui/components/filters/PriceFilterSlider';
import { FilterToggleType } from '@/app/ui/components/filters/FilterToggle';
import SaleFilter from '@/app/ui/components/filters/SaleFilter';
import CouponFilter from '@/app/ui/components/filters/CouponFilter';
import HottestSaleFilter from '@/app/ui/components/filters/HottestSaleFilter';

interface Props {
  filter: FilterOptionValue;
  onbackClick: () => void;
  hasSearch: boolean;
  productCounters: number | undefined;
  onFilterUpdate: (searchParamKey: FilterOptionKeys, value: string[] | number[] | number) => void;
  isTopFilter?: boolean;
  onCloseModal?: () => void;
  facets: FilterOptions | null;
  disabledFilters?: FilterOptionKeys[];
  searchParams?: SearchParams;
  blackFriday?: boolean | undefined;
}

const getSelectedOptions = (options: MappedFacet[]) => {
  return options.filter((option) => option.checked).map((option) => option.value);
};

const SubFilter = ({
  filter,
  onbackClick,
  hasSearch,
  onFilterUpdate,
  productCounters,
  isTopFilter,
  onCloseModal,
  facets,
  searchParams,
  disabledFilters,
  blackFriday,
}: Props) => {
  const { t } = useTranslation(['filters']);
  const [searchInput, setSearchInput] = useState('');

  const [filterOptions, setFilterOptions] = useState([] as MappedFacet[]);
  const [selectedOptions, setSelectedOptions] = useState(getSelectedOptions(filterOptions) as string[]);
  const showSale =
    (!!facets?.sale?.options?.length || !!facets?.sale?.options?.filter((el) => el.id !== 'sale').length) &&
    !searchParams?.filters?.hottest_sale;
  const showHottestSale = !!facets?.hottest_sale?.options?.length && !!blackFriday;
  const showCoupon = !!facets?.coupon?.options?.length;

  useEffect(() => {
    if (filter && filter.options.length) {
      setFilterOptions(filter.options.sort((a, b) => a.name?.localeCompare(b.name)));
      setSelectedOptions(getSelectedOptions(filter.options));
    }
  }, [filter]);

  function onExitFilter() {
    if (filter?.name === FilterOptionKeys.PRICE) {
      onbackClick();
    } else {
      if (filter?.name) onFilterUpdate(filter?.name, selectedOptions);
      onbackClick();
    }
  }

  function onSelect(id: string) {
    //update selected options
    let selected = selectedOptions;
    if (selectedOptions.includes(id)) {
      selected = selectedOptions.filter((option) => option !== id);
    } else {
      selected = [...selectedOptions, id];
    }
    setSelectedOptions(selected);

    const newFilterOptions = filterOptions.map((option) => {
      return option.id === id ? { ...option, checked: !option.checked } : option;
    });

    setFilterOptions(newFilterOptions);

    //apply filter immediately in the background
    onFilterUpdate(filter?.name, selected);
  }

  const renderCheckMark = (checkMarkColor: string) => (
    <div className={`position-absolute`} style={{ zIndex: 2 }}>
      <Check color={checkMarkColor} size={40} />
    </div>
  );

  function renderColorOptions(options: MappedFacet[]) {
    return options.map((option) => {
      const checkMarkColor = option.id === 'white' ? 'black' : 'white';
      return (
        <div
          onClick={() => onSelect(option.id)}
          key={option.id}
          style={{
            flex: '1 1 25%',
            textTransform: 'capitalize',
            maxWidth: '25%',
            boxSizing: 'border-box',
            padding: '0.625rem',
          }}>
          <div>
            <div className="d-flex justify-content-center align-items-center position-relative">
              {option.checked && renderCheckMark(checkMarkColor)}
              <ColorBox color={option.value} isMobile={true} />
            </div>
          </div>
          <div className="d-flex justify-content-center mt-1" style={{ fontSize: '0.8rem' }}>
            {option.name.capitalizeWords()}
          </div>
        </div>
      );
    });
  }

  function renderOptions(name: FilterOptionKeys | undefined, options: MappedFacet[]) {
    if (name === FilterOptionKeys.COLORS) {
      return <div className="d-flex flex-wrap">{renderColorOptions(options)}</div>;
    }

    if (name === FilterOptionKeys.PRICE) {
      return (
        <>
          <PriceFilterSlider
            price={facets?.price}
            onFilterUpdate={(value) => onFilterUpdate(FilterOptionKeys.PRICE, value)}
          />
          <div className="mt-3 mb-3 d-flex align-items-center gap-3">
            {showSale && !disabledFilters?.includes(FilterOptionKeys.SALE) && (
              <SaleFilter isActive={!!searchParams?.filters?.sale} type={FilterToggleType.CLEAR} />
            )}
            {showHottestSale && !disabledFilters?.includes(FilterOptionKeys.HOTTEST_SALE) && (
              <HottestSaleFilter isActive={!!searchParams?.filters?.hottest_sale} type={FilterToggleType.CLEAR} />
            )}
            {showCoupon && <CouponFilter isActive={!!searchParams?.filters?.coupon} type={FilterToggleType.CLEAR} />}
          </div>
        </>
      );
    }

    return options?.map((option) => (
      <div onClick={() => onSelect(option.id)} key={option.id} className={styles.subcategoryoption}>
        <input
          className="cursor-pointer"
          id="camouflage"
          name="camouflage"
          checked={option.checked}
          type="checkbox"
          readOnly={true}
          style={{
            display: 'initial',
            accentColor: 'transparent',
            marginRight: 18,
            marginTop: '0.47rem',
          }}
        />
        <label className={`${clsx(option.checked ? 'fw-bold' : '')}`}>
          <span>{option.name.capitalizeWords()}</span>
          <span className={`${styles.filterbadgegray} ms-2`}>{option.count}</span>
        </label>
      </div>
    ));
  }

  function filterBySearchInput(value: string) {
    const newFilterOptions = filter?.options?.filter((option) =>
      option.name.toLowerCase().includes(value.toLowerCase())
    );

    setFilterOptions(newFilterOptions);
  }

  function onSearchInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchInput(e.target.value);
    filterBySearchInput(e.target.value);
  }

  return (
    <div className="bg-white w-100 h-100 fixed-top overflow-auto">
      <div className="d-flex aligncenter justify-content-between mt-3 mx-4 my-3">
        {!isTopFilter && <ChevronLeft size={20} onClick={onExitFilter} />}
        <span className="aligncenter" style={{ fontSize: '1.25rem', fontWeight: 700 }}>
          {t(filter?.name ?? '')}
        </span>
        {isTopFilter && (
          <Image onClick={onCloseModal} src="/intstatic/icons/close.svg" alt="Close" height={14} width={14} />
        )}
      </div>
      <Divider />
      <div style={{ height: 729 }}>
        <div className="pt-4 px-4 pb-5">
          {hasSearch && (
            <input
              onChange={onSearchInputChange}
              className={styles.subfiltersearchinput}
              placeholder={t('SEARCH')}
              type="text"
              autoCorrect="off"
              enterKeyHint="search"
              value={searchInput}
            />
          )}

          <div className="mt-2">{renderOptions(filter?.name, filterOptions)}</div>
          <div className={styles.filterbuttonapply}>
            <div className="row">
              <div className={'col-12'}>
                <button
                  className="btn btn-black lowercase full-width"
                  onClick={isTopFilter ? onCloseModal : onExitFilter}>
                  {t('DONE')}
                  <span className="ms-1">({productCounters})</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubFilter;
